import React from 'react';
import {
  Button,
  Checkbox,
  Chip,
  Container,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip
} from '@mui/material';
import { V4_BUTTON_DEFAULT, V4_ICON_BUTTON, V4_ICON_BUTTON_SMALL, V4_TABLE } from '../../theme/v4Compat';
import { useNavigate } from 'react-router-dom';
import { extractApiErrorMessage, notifications } from '../../utils';
import { Title, Loader, ClearFilters, LegacyGrid as Grid } from '../../elements';
import { UpdateLocationModal, LocationSupplyFeedModal, LocationVaccinationModal } from '../../components';
import { AnimalType } from '../../models/animal';
import useAuthContext from '../../hooks/useAuthContext';
import SearchIcon from '@mui/icons-material/Search';
import ListIcon from '@mui/icons-material/List';
import { LocationsService } from '../../hooks/LocationsService';
import { LocationViewModel, LocationPosition, LocationStable, LocationAreaViewModel } from '../../models/locations';
import { AnimalTypeSelect, LocationPositionSelect, LocationStableSelect, TablePaginationWrapper } from '../../elements';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faTemperatureHigh, faSyringe } from '@fortawesome/free-solid-svg-icons';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import SquareMetersPerPigChip from './SquareMetersPerPigChip';
import { LocationSupplyFeedModalResult } from '../LocationSupplyFeedModal/component.tsx';
import { LocationVaccinationModalResult } from '../LocationVaccinationModal/component.tsx';
import { InjectionsService } from '../../hooks';
import { useTranslation } from 'react-i18next';
import { AssessmentOutlined } from '@mui/icons-material';
import UserRole from '../../constants/user-role';
import useFeatures, { FeatureType } from '../../hooks/features';

interface IProps {
  onLocationsSelected: (locations: LocationViewModel[]) => void;
  mode: 'page' | 'search';
}

const FarmLocations: React.FC<any> = ({ mode, onLocationsSelected }: IProps) => {
  if (!mode) {
    mode = 'page'; // 'page' mode is default
  }
  const locationsService = new LocationsService();
  const injectionsService = new InjectionsService();
  const navigate = useNavigate();
  const { state } = useAuthContext();
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [locations, setLocations] = React.useState([] as LocationViewModel[]);
  const [lastLocation, setLastLocation] = React.useState({} as LocationViewModel);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedLocation, setSelectedLocation] = React.useState({} as LocationViewModel);

  const updateLocationModalRef = React.useRef<HTMLElement>(null!);
  const LocationSupplyFeedModalRef = React.useRef<HTMLElement>(null!);
  const LocationVaccinationModalRef = React.useRef<HTMLElement>(null!);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);
  const { hasFeature } = useFeatures();

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setSelectedIds([]);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const [animalType, setAnimalType] = React.useState(0);
  const [locationPosition, setLocationPosition] = React.useState(0);
  const [locationStable, setLocationStable] = React.useState(0);

  // location number text control
  const [number, setNumber] = React.useState('');

  const handleLocationNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNumber(event.target.value);
  };
  // * location number text control
  // location room text control
  const [room, setRoom] = React.useState('');

  const handleLocationRoomChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRoom(event.target.value);
  };
  // * location room text control
  // location pen text control
  const [pen, setPen] = React.useState('');

  const handleLocationPenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPen(event.target.value);
  };
  // * location pen text control

  const [sortField, setSortField] = React.useState<string | null>(null);
  const [sortDescending, setSortDescending] = React.useState(false);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDescending((prev) => !prev);
    } else {
      setSortField(field);
      setSortDescending(false);
    }
    setPage(0);
    setLoaded(false);
  };

  const buildSortParam = (): string | undefined => {
    if (!sortField) return undefined;
    return sortDescending ? `${sortField}-desc` : sortField;
  };

  const loadFarmLocations = async () => {
    setIsBusy(true);
    setTotal(0);

    const searchPage = page === 0 ? 1 : page + 1;

    locationsService
      .getLocationsByQuery(
        searchPage,
        rowsPerPage,
        number,
        room,
        pen,
        locationPosition,
        locationStable,
        animalType,
        buildSortParam()
      )
      .then((result) => {
        // A 2xx without `data` reached production and crashed the page on `locations.length`.
        setTotal(result?.total ?? 0);
        setLocations(result?.data ?? []);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      const loadData = async () => loadFarmLocations();
      loadData();
    }
  }, [loaded]);

  const openCreateLocationModal = () => {
    setSelectedLocation({
      ...lastLocation,
      farmId: state.farmId
    } as LocationViewModel);

    setDialogOpen(true);
  };

  const renderAnimalTypeText = (animalType: AnimalType) => {
    if (animalType === AnimalType.Unknown) {
      return '-';
    }
    return AnimalType[animalType];
  };

  const renderLocationStableText = (stable: LocationStable) => {
    if (stable === LocationStable.Unknown) {
      return '-';
    }
    return LocationStable[stable];
  };

  const renderLocationAreaText = (area: LocationAreaViewModel | undefined) => {
    if (area === null || area === undefined) {
      return '';
    }

    return t('f-loc-area-value', { value: area.areaInM || 0 });
  };

  // create & update location handler
  const onUpdateLocation = (location: LocationViewModel) => {
    if (location === null) {
      setSelectedLocation({} as LocationViewModel);
    }

    if (location != null && location.id) {
      locationsService
        .updateLocation(location)
        .then(() => {
          notifications.success(t('f-loc-upd-ok'));
          setLoaded(false);
        })
        .catch((e) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        });
    }

    if (location != null && !location.id) {
      locationsService
        .createLocation(location)
        .then((result) => {
          if (result.success.length) {
            notifications.success(t('f-loc-create-ok'));
            setLoaded(false);
          } else {
            notifications.error(t('f-loc-create-error'));
          }
        })
        .catch((e) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        });
    }

    setDialogOpen(false);
    setLastLocation(location);
  };

  const querySearch = () => {
    setPage(0);
    setLoaded(false);
  };

  const clearFilters = () => {
    setNumber('');
    setRoom('');
    setPen('');
    setAnimalType(0);
    setLocationPosition(0);
    setLocationStable(0);
  };

  const viewAnalytics = () => {
    const selectedLocations = locations
      .filter((x) => selectedIds.includes(x.id))
      .map((x) => {
        return {
          id: x.id,
          name: x.room
        };
      });

    navigate(`/analytics/${JSON.stringify(selectedLocations)}`);
  };

  const viewLocation = (id: string) => {
    navigate(`/locations/${id}`);
  };

  const viewClimate = (id: string) => {
    navigate(`/locations/${id}/climate`);
  };

  // location multiple select
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = locations.map((x) => x.id);
      setSelectedLocationsByIds(newSelected);
      return;
    }
    setSelectedLocationsByIds([]);
  };

  const isSelected = (id: string) => selectedIds.indexOf(id) !== -1;

  const handleSelectLocation = (_event: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selectedIds.indexOf(id);
    let newSelectedIds: string[] = [];

    if (selectedIndex === -1) {
      newSelectedIds = newSelectedIds.concat(selectedIds, id);
    } else if (selectedIndex === 0) {
      newSelectedIds = newSelectedIds.concat(selectedIds.slice(1));
    } else if (selectedIndex === selectedIds.length - 1) {
      newSelectedIds = newSelectedIds.concat(selectedIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedIds = newSelectedIds.concat(selectedIds.slice(0, selectedIndex), selectedIds.slice(selectedIndex + 1));
    }

    setSelectedLocationsByIds(newSelectedIds);
  };

  const setSelectedLocationsByIds = (locationIds: string[]) => {
    setSelectedIds(locationIds);

    const filtered = locations.filter((x) => locationIds.includes(x.id));

    if (onLocationsSelected) {
      onLocationsSelected(filtered);
    }
  };
  // * location multiple select

  // medical supply oral feed modal
  const [supplyFeedDialogOpen, setSupplyFeedDialogOpen] = React.useState(false);

  const handleFeedMedicalSupply = (result: LocationSupplyFeedModalResult | null) => {
    setSupplyFeedDialogOpen(false);

    if (result === null) {
      return;
    }

    injectionsService
      .createOralFeedInjection(result.farmSupplyId, selectedIds, result.repeatDays, result.dose)
      .then(() => {
        notifications.success(t('f-injections-oral-ok'));
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };
  // * medical supply oral feed modal

  // gilts vaccination modal
  const [vaccinationDialogOpen, setVaccinationDialogOpen] = React.useState(false);

  const handleVaccinateGilts = (result: LocationVaccinationModalResult | null) => {
    setVaccinationDialogOpen(false);

    if (result === null) {
      return;
    }

    injectionsService
      .createVaccinationInjection(result.farmSupplyId, selectedIds, result.repeatDays, result.dose)
      .then(({ injectionsCreated, skippedLocationIds }) => {
        if (injectionsCreated.length === 0) {
          notifications.warning(t('f-loc-vaccination-no-gilts'));
          return;
        }

        notifications.success(t('f-loc-vaccination-ok'));

        if (skippedLocationIds.length > 0) {
          notifications.warning(t('f-loc-vaccination-skipped', { count: skippedLocationIds.length }));
        }
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };
  // * gilts vaccination modal

  const getAreaComplianceClass = (isAreaCompliant: boolean): string => {
    if (!hasFeature(FeatureType.USE_WELFARE_AREA_CHECKING)) return '';
    // Important utilities: TableCell sets its own colour. #e57373 is v4's error.light (v9's is #ef5350).
    return isAreaCompliant === false ? '!font-bold !text-[#e57373]' : '';
  };

  const renderInactiveStatus = (isSoftDeleted: boolean) => {
    if (!isSoftDeleted) {
      return null;
    }

    return (
      <Chip
        size="small"
        label={t('f-loc-inactive')}
        sx={{ bgcolor: 'grey.400', color: 'common.white', fontSize: '0.7rem', px: 1, py: 0.25 }}
      />
    );
  };

  return (
    <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
      <Grid container spacing={2} id="farmLocationsComponent">
        <Grid item container direction="row" sx={{ justifyContent: 'space-between' }}>
          <Grid item container md={6} xs={8} spacing={1}>
            <Grid item>
              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-brand" size="2x" />
            </Grid>
            <Grid item>
              <Title>{t('f-loc-search-title')}</Title>
            </Grid>
          </Grid>

          {mode === 'page' && (state.role === UserRole.manager || state.role === UserRole.admin) && (
            <Grid item>
              <Tooltip title={t('f-com-create')}>
                <IconButton
                  id="createLocationBtn"
                  color="primary"
                  sx={V4_ICON_BUTTON}
                  onClick={openCreateLocationModal}
                >
                  <AddCircleIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        </Grid>

        {/* filters section */}
        <Grid item container direction="row" spacing={2}>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <TextField
              id="locationNumberField"
              label={t('f-chip-number')}
              variant="outlined"
              size="small"
              fullWidth
              value={number}
              onChange={handleLocationNumberChange}
            />
          </Grid>

          <Grid item lg={3} md={4} sm={6} xs={12}>
            <TextField
              id="locationRoomField"
              label={t('f-loc-room')}
              variant="outlined"
              size="small"
              fullWidth
              value={room}
              onChange={handleLocationRoomChange}
            />
          </Grid>

          <Grid item lg={3} md={4} sm={6} xs={12}>
            <TextField
              id="locationPenField"
              label={t('f-loc-pen')}
              variant="outlined"
              size="small"
              fullWidth
              value={pen}
              onChange={handleLocationPenChange}
            />
          </Grid>

          <Grid item lg={3} md={4} sm={6} xs={12}>
            <LocationPositionSelect
              value={locationPosition}
              fullWidth
              allowAny
              onLocationPositionChange={(value) => setLocationPosition(value)}
            />
          </Grid>

          <Grid item lg={3} md={4} sm={6} xs={12}>
            <LocationStableSelect
              value={locationStable}
              fullWidth
              allowAny
              onLocationStableChange={(value) => setLocationStable(value)}
            />
          </Grid>

          <Grid item lg={3} md={4} sm={6} xs={12}>
            <AnimalTypeSelect
              value={animalType}
              fullWidth
              allowAny
              onAnimalTypeChange={(type) => setAnimalType(type)}
            />
          </Grid>

          <Grid item lg={3} md={4} sm={6} xs={12}>
            <div className="flex items-center">
              <ClearFilters onClick={clearFilters} />

              <Button
                id="searchBtn"
                variant="contained"
                color="inherit"
                sx={V4_BUTTON_DEFAULT}
                fullWidth
                onClick={querySearch}
                startIcon={<SearchIcon />}
              >
                {t('f-search')}
              </Button>
            </div>
          </Grid>
        </Grid>

        <Grid item container direction="row" spacing={1} sx={{ alignItems: 'center' }}></Grid>
        {/* filters section */}

        <Grid item container sx={{ mt: 1 }} alignItems="center">
          <Grid item spacing={1} container lg={6} md={6} xs={12}>
            <ListIcon color="primary" fontSize="large" />
            <div className="ml-2">
              <Title>{t('f-search-results')}</Title>
            </div>
            <Grid item>
              <Chip label={total} variant="outlined" />
            </Grid>
          </Grid>

          {mode === 'page' && (
            <Grid item container lg={6} md={6} sm={12} spacing={1}>
              <Grid item sm={4} xs={12}>
                <Button
                  disabled={selectedIds.length !== 3}
                  id="statisticsBtn"
                  color="primary"
                  fullWidth
                  variant="contained"
                  startIcon={<AssessmentOutlined />}
                  onClick={() => viewAnalytics()}
                >
                  Analytics
                </Button>
              </Grid>
              <Grid item sm={4} xs={12}>
                <Button
                  disabled={selectedIds.length === 0}
                  id="feedSupplyBtn"
                  color="primary"
                  fullWidth
                  variant="contained"
                  startIcon={<Icon>{'bubble_chart'}</Icon>}
                  onClick={() => setSupplyFeedDialogOpen(true)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('f-loc-view-medication-supply')}
                </Button>
              </Grid>
              <Grid item sm={4} xs={12}>
                <Button
                  disabled={selectedIds.length === 0}
                  id="vaccinateGiltsBtn"
                  color="primary"
                  fullWidth
                  variant="contained"
                  startIcon={<FontAwesomeIcon icon={faSyringe} />}
                  onClick={() => setVaccinationDialogOpen(true)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('f-loc-vaccination-action')}
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>

        <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

        {!isBusy && (
          <Grid item xs={12} sx={{ mt: 1 }}>
            <TablePaginationWrapper
              rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            >
              <TableContainer component={Paper} elevation={1}>
                <Table size="small" sx={V4_TABLE} id="locationsSearchResultTable">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Tooltip arrow title={t('f-loc-search-select-all')}>
                          <Checkbox
                            color="primary"
                            indeterminate={selectedIds.length > 0 && selectedIds.length < locations.length}
                            checked={locations.length > 0 && selectedIds.length === locations.length}
                            onChange={handleSelectAllClick}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          hideSortIcon
                          active={sortField === 'room'}
                          direction={sortField === 'room' && sortDescending ? 'desc' : 'asc'}
                          onClick={() => handleSort('room')}
                        >
                          {t('f-loc-room')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          hideSortIcon
                          active={sortField === 'pen'}
                          direction={sortField === 'pen' && sortDescending ? 'desc' : 'asc'}
                          onClick={() => handleSort('pen')}
                        >
                          {t('f-loc-pen')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>{t('f-loc-area')}</TableCell>
                      <TableCell>{t('f-loc-pos')}</TableCell>
                      <TableCell>
                        <TableSortLabel
                          hideSortIcon
                          active={sortField === 'stable'}
                          direction={sortField === 'stable' && sortDescending ? 'desc' : 'asc'}
                          onClick={() => handleSort('stable')}
                        >
                          {t('f-loc-stable')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          hideSortIcon
                          active={sortField === 'animalType'}
                          direction={sortField === 'animalType' && sortDescending ? 'desc' : 'asc'}
                          onClick={() => handleSort('animalType')}
                        >
                          {t('f-com-animal-type')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>{t('f-loc-cyc-animal-amount')}</TableCell>
                      <TableCell>
                        <TableSortLabel
                          hideSortIcon
                          active={sortField === 'number'}
                          direction={sortField === 'number' && sortDescending ? 'desc' : 'asc'}
                          onClick={() => handleSort('number')}
                        >
                          {t('f-chip-number')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>{t('f-square-meters-per-pig')}</TableCell>
                      <TableCell>{t('f-com-status')}</TableCell>
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  {loaded && locations.length > 0 && (
                    <TableBody>
                      {locations.map((row) => {
                        const isItemSelected = isSelected(row.id);
                        const labelId = `location-checkbox-${row.number}`;

                        return (
                          <TableRow key={row.id} hover sx={row.isSoftDeleted ? INACTIVE_ROW : undefined}>
                            <TableCell padding="checkbox">
                              <Tooltip arrow title={t('f-loc-search-select')}>
                                <Checkbox
                                  checked={isItemSelected}
                                  color="primary"
                                  onClick={(event) => handleSelectLocation(event, row.id)}
                                  slotProps={{ input: { 'aria-labelledby': labelId } }}
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell>{row.room}</TableCell>
                            <TableCell>{row.pen}</TableCell>
                            <TableCell className={getAreaComplianceClass(row.isAreaCompliant)}>
                              {renderLocationAreaText(row.area)}
                            </TableCell>
                            <TableCell>{LocationPosition[row.positioning]}</TableCell>
                            <TableCell>{renderLocationStableText(row.stable)}</TableCell>
                            <TableCell>{renderAnimalTypeText(row.animalType)}</TableCell>
                            <TableCell>{row.animalsCount}</TableCell>
                            <TableCell>{row.number}</TableCell>
                            <TableCell>
                              <SquareMetersPerPigChip areaInM={row.area?.areaInM} animalsCount={row.animalsCount} />
                            </TableCell>
                            <TableCell>{renderInactiveStatus(row.isSoftDeleted)}</TableCell>
                            {/* Gated on `hasSensorDevice`, which the location payload now carries. The old
                                condition was `metadata.climateDeviceNumber` - a field nothing writes any
                                more, now that a device is bound to a pen through the Sensor devices screen.
                                The flag rather than a lookup on purpose: the sensor-device endpoints are
                                Admin + Manager only, and this table is also shown to farm users and
                                veterinarians. The tooltip names the device, so the chart the link opens is
                                attributable to something before it is opened. */}
                            <TableCell>
                              {row.hasSensorDevice && (
                                <Tooltip
                                  arrow
                                  title={
                                    row.sensorDevice?.name
                                      ? t('f-loc-climate-device', { device: row.sensorDevice.name })
                                      : t('f-loc-climate')
                                  }
                                >
                                  <span>
                                    <IconButton
                                      color="secondary"
                                      size="small"
                                      sx={V4_ICON_BUTTON_SMALL}
                                      disabled={mode === 'search'}
                                      onClick={() => viewClimate(row.id)}
                                    >
                                      <FontAwesomeIcon icon={faTemperatureHigh} className="text-brand" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>

                            <TableCell>
                              <Tooltip arrow title={t('f-loc-view')}>
                                <span>
                                  <IconButton
                                    color="primary"
                                    size="small"
                                    sx={V4_ICON_BUTTON_SMALL}
                                    disabled={mode === 'search'}
                                    onClick={() => viewLocation(row.id)}
                                  >
                                    <Icon>{'visibility'}</Icon>
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </TablePaginationWrapper>
          </Grid>
        )}

        <UpdateLocationModal
          ref={updateLocationModalRef}
          location={selectedLocation}
          opened={dialogOpen}
          showLastLocationMessage={lastLocation && !!lastLocation.farmId}
          onLocationUpdated={(location: LocationViewModel) => onUpdateLocation(location)}
        />

        <LocationSupplyFeedModal
          ref={LocationSupplyFeedModalRef}
          opened={supplyFeedDialogOpen}
          onModalAction={handleFeedMedicalSupply}
        />

        <LocationVaccinationModal
          ref={LocationVaccinationModalRef}
          opened={vaccinationDialogOpen}
          onModalAction={handleVaccinateGilts}
        />
      </Grid>
    </Container>
  );
};

export default FarmLocations;

/** A soft-deleted location. An `sx`, not a class, so the row's hover shade still wins over it. */
const INACTIVE_ROW = { opacity: 0.6, backgroundColor: 'grey.50' } as const;
